/**
 * <Field> — keystone form primitive.
 *
 * Wraps a single form control (Input / Textarea / Select / Checkbox /
 * Radio / Toggle) with its label, helper text, and error message. Auto-
 * generates an id via React.useId() and threads it to the wrapped control
 * + label so htmlFor / aria-describedby / aria-invalid / aria-required
 * are correct without per-call-site boilerplate.
 *
 * Resolves Phase-1 finding C6: ~80% of forms lack htmlFor/id pairing.
 * Once Phase 6 ships, every new form goes through <Field> and the
 * label-pairing problem cannot recur.
 *
 * Composition:
 *
 *   <Field label="Email" required helper="We'll never share it.">
 *     <Input type="email" name="email" />
 *   </Field>
 *
 *   <Field label="Notes" error={errors.notes}>
 *     <Textarea name="notes" rows={4} />
 *   </Field>
 *
 * Spec: docs/design-system/06-buttons-forms.md.
 */

import { forwardRef, useId, useMemo, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Text } from '../../primitives/Text';
import { FieldContext } from './FieldContext';

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Label text. Pass a custom node when label needs formatting. */
  label?: ReactNode;
  /** Helper text shown beneath the input when no error is present. */
  helper?: ReactNode;
  /** Error message. When set, the field renders in invalid state. */
  error?: ReactNode;
  /** Adds the required asterisk and aria-required. */
  required?: boolean;
  /** Disable the wrapped input. The control inherits this via context. */
  disabled?: boolean;
  /**
   * Hide the label visually but keep it for screen readers.
   * Useful in dense table-row inline editors.
   */
  visuallyHiddenLabel?: boolean;
  /** Layout — vertical (default) or horizontal (label left, input right). */
  layout?: 'vertical' | 'horizontal';
  /** Override the auto-generated id (rare). */
  id?: string;
  children: ReactNode;
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  {
    label,
    helper,
    error,
    required = false,
    disabled = false,
    visuallyHiddenLabel = false,
    layout = 'vertical',
    id: idOverride,
    className,
    children,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = idOverride ?? `field-${generatedId}`;
  const helperId = helper ? `${inputId}-helper` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const invalid = !!error;

  const ctx = useMemo(
    () => ({ inputId, helperId, errorId, invalid, required, disabled }),
    [inputId, helperId, errorId, invalid, required, disabled],
  );

  return (
    <FieldContext.Provider value={ctx}>
      <div
        ref={ref}
        className={cn(
          'flex',
          layout === 'horizontal' ? 'flex-row items-center gap-3' : 'flex-col gap-1.5',
          className,
        )}
        {...rest}
      >
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'text-sm font-medium text-text',
              layout === 'horizontal' && 'min-w-[140px]',
              visuallyHiddenLabel && 'sr-only',
            )}
          >
            {label}
            {required && (
              <span className="text-danger ml-0.5" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        <div className={cn('flex flex-col gap-1', layout === 'horizontal' && 'flex-1 min-w-0')}>
          {children}
          {error ? (
            <Text id={errorId} variant="validation" tone="danger" role="alert">
              {error}
            </Text>
          ) : helper ? (
            <Text id={helperId} variant="caption" tone="muted">
              {helper}
            </Text>
          ) : null}
        </div>
      </div>
    </FieldContext.Provider>
  );
});

Field.displayName = 'Field';

export default Field;
